// src/modules/stores/services/store.service.ts

import { API_URL, API_HEADER } from "@modules/shared/enums/common-enum";
import { fetchWithAuth } from "@modules/shared/helpers/fetch-with-auth.helper";
import MediaService from "@modules/media/services/media.service";
import {
  StoreDto,
  ShopDto,
  CreateStoreInput,
  UpdateStoreInput,
  CreateShopInput,
  UpdateShopInput,
  mapStoreDto,
  mapShopDto,
} from "../models/store.model";
import {
  fetchAllPaginated,
  buildPagingQuery,
  shouldContinuePaging,
  extractList,
  unwrapEntity,
} from "@modules/shared/services/pagination.service";

type AuthArgs = {
  auth: any;
  setAuth: (auth: any) => void;
  logout: () => void;
};

export class StoreService {
  private readonly mediaService = new MediaService();

  // ===== Store (filter business_model=store) =====
  async getAllStores(args: AuthArgs): Promise<StoreDto[]> {
    return fetchAllPaginated<StoreDto>(async (page, limit) => {
      const response = await fetchWithAuth(
        `${API_URL.B2B}?${buildPagingQuery(page, limit, -1)}&business_model=store`,
        { method: "GET", headers: API_HEADER },
        args.auth,
        args.setAuth,
        args.logout,
      );
      const items = extractList(response).map(mapStoreDto);
      const hasMore = shouldContinuePaging(items, page, limit, response);
      return { items, hasMore };
    });
  }

  async getStoreById(id: string, args: AuthArgs): Promise<StoreDto | null> {
    try {
      const response = await fetchWithAuth(
        `${API_URL.B2B}/${encodeURIComponent(id)}`,
        { method: "GET", headers: API_HEADER },
        args.auth,
        args.setAuth,
        args.logout,
      );
      return mapStoreDto(response.data ?? response);
    } catch {
      return null;
    }
  }

  async createStore(
    input: CreateStoreInput,
    args: AuthArgs,
  ): Promise<StoreDto> {
    const response = await fetchWithAuth(
      API_URL.B2B,
      {
        method: "POST",
        headers: API_HEADER,
        body: JSON.stringify({
          name: input.name.trim(),
          owner_id: input.owner_id.trim(),
          members: input.members.map((m) => m.trim()).filter(Boolean),
          description: input.description.trim(),
          show_social: input.show_social,
          business_model: "store",
        }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapStoreDto(unwrapEntity(response));
  }

  async updateStore(
    id: string,
    input: UpdateStoreInput,
    args: AuthArgs,
  ): Promise<StoreDto> {
    const response = await fetchWithAuth(
      `${API_URL.B2B}/${encodeURIComponent(id)}`,
      {
        method: "PUT",
        headers: API_HEADER,
        body: JSON.stringify({
          name: input.name?.trim(),
          description: input.description?.trim(),
          show_social: input.show_social,
        }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapStoreDto(unwrapEntity(response));
  }

  async deleteStore(id: string, args: AuthArgs): Promise<boolean> {
    const response = await fetchWithAuth(
      `${API_URL.B2B}/${encodeURIComponent(id)}`,
      { method: "DELETE", headers: API_HEADER },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return typeof response === "boolean"
      ? response
      : Boolean(response?.success ?? response);
  }

  // ===== Shop =====
  async getAllShops(args: AuthArgs): Promise<ShopDto[]> {
    return fetchAllPaginated<ShopDto>(async (page, limit) => {
      const response = await fetchWithAuth(
        `${API_URL.MARKETPLACE_SHOP}?${buildPagingQuery(page, limit, -1)}`,
        { method: "GET", headers: API_HEADER },
        args.auth,
        args.setAuth,
        args.logout,
      );
      const items = extractList(response).map(mapShopDto);
      const hasMore = shouldContinuePaging(items, page, limit, response);
      return { items, hasMore };
    });
  }

  async getShopsByStore(storeId: string, args: AuthArgs): Promise<ShopDto[]> {
    const all = await this.getAllShops(args);
    return all.filter((shop) => shop.b2b_id === storeId);
  }

  async createShop(input: CreateShopInput, args: AuthArgs): Promise<ShopDto> {
    let imageUrl = input.image_url?.trim() || "";
    if (input.image_file) {
      imageUrl = await this.uploadFile(input.image_file, args);
    }

    let bannerUrl = input.banner_url?.trim() || "";
    if (input.banner_file) {
      bannerUrl = await this.uploadFile(input.banner_file, args);
    }

    const response = await fetchWithAuth(
      API_URL.MARKETPLACE_SHOP,
      {
        method: "POST",
        headers: API_HEADER,
        body: JSON.stringify({
          name: input.name.trim(),
          location: input.location.trim(),
          b2b_id: input.b2b_id,
          image: imageUrl || undefined,
          banner: bannerUrl || undefined,
          is_active: input.is_active !== undefined ? input.is_active : true,
        }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapShopDto(unwrapEntity(response));
  }

  async updateShop(input: UpdateShopInput, args: AuthArgs): Promise<ShopDto> {
    let imageUrl = input.image_url?.trim() || "";
    if (input.image_file) {
      imageUrl = await this.uploadFile(input.image_file, args);
    }

    let bannerUrl = input.banner_url?.trim() || "";
    if (input.banner_file) {
      bannerUrl = await this.uploadFile(input.banner_file, args);
    }

    const response = await fetchWithAuth(
      API_URL.MARKETPLACE_SHOP,
      {
        method: "PUT",
        headers: API_HEADER,
        body: JSON.stringify({
          id: input.shop_id,
          name: input.name.trim(),
          location: input.location.trim(),
          image: imageUrl || null,
          banner: bannerUrl || null,
          is_active: input.is_active,
        }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapShopDto(unwrapEntity(response));
  }

  async deleteShop(shopId: string, args: AuthArgs): Promise<boolean> {
    const response = await fetchWithAuth(
      `${API_URL.MARKETPLACE_SHOP}/${encodeURIComponent(shopId)}`,
      { method: "DELETE", headers: API_HEADER },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return typeof response === "boolean"
      ? response
      : Boolean(response?.success ?? response);
  }

  async getShopCount(args: AuthArgs): Promise<number> {
    try {
      const response = await fetchWithAuth(
        `${API_URL.MARKETPLACE_SHOP}?limit=1&page=1`,
        { method: "GET", headers: API_HEADER },
        args.auth,
        args.setAuth,
        args.logout,
      );
      const total = response?.pagination?.total ?? 0;
      return total;
    } catch (error) {
      console.error("getShopCount error:", error);
      const shops = await this.getAllShops(args);
      return shops.length;
    }
  }

  // ----- Helper: upload file -----
  private async uploadFile(file: File, args: AuthArgs): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await this.mediaService.uploadFile(
      formData,
      args.auth,
      args.setAuth,
      args.logout,
    );
    return (response as any)?.data?.url ?? (response as any)?.url ?? "";
  }
}
